import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, HostListener, SimpleChanges, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, Observable, of, forkJoin } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { AdminTreeNode } from '../../../services/admin.service';
import { BucketService, ProcessedFile, BucketHierarchyNode, BucketHierarchyResponse } from '../../../services/bucket.service';
import { ImageProcessingService, ProcessingImage } from '../../../services/image-processing.service';
import { CoordinateTransformationService, LineData, Coordinates } from '../../../services/coordinate-transformation.service';
import { UploadProgressService } from '../../../services/upload-progress.service';
import { ImageProcessingToolComponent } from '../../../admin/image-processing-tool/image-processing-tool.component';
import { UploadProgressPopupComponent } from '../upload-progress-popup/upload-progress-popup.component';
import { environment } from '../../../../environments/environment';

export interface BreadcrumbItem {
  name: string;
  icon: string;
  type: 'operation' | 'action';
}

@Component({
  selector: 'app-admin-main-area',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDividerModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatBadgeModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule,
    ImageProcessingToolComponent,
    UploadProgressPopupComponent
  ],
  templateUrl: './admin-main-area.component.html',
  styleUrl: './admin-main-area.component.css'
})
export class AdminMainAreaComponent implements OnInit, OnDestroy {
  @Input() selectedNode: AdminTreeNode | null = null;
  @Input() bucketFolders: string[] = [];
  @Input() selectedFolder: string = '';
  @Input() processedFiles: ProcessedFile[] = [];
  @Input() isLoading: boolean = false;
  @Input() operationResult: string = '';
  @Input() showDetails: boolean = true;
  
  @Output() folderSelected = new EventEmitter<string>();
  @Output() folderProcessed = new EventEmitter<string>();
  @Output() fileSelected = new EventEmitter<ProcessedFile>();
  @Output() showDetailsToggled = new EventEmitter<boolean>();
  @Output() bucketsRefreshed = new EventEmitter<void>();

  // Breadcrumb navigation
  breadcrumbItems: BreadcrumbItem[] = [];
  
  // State management
  selectedFile: ProcessedFile | null = null;
  
  // Bucket management
  bucketName: string = 'questions-images-bucket';
  showProcessedFiles: boolean = false;
  
  // Image Processing state
  processingImages: ProcessingImage[] = [];
  currentImageIndex: number = 0;
  currentImage: ProcessingImage | null = null;
  imageProcessingMode: boolean = false;
  isLoadingImages: boolean = false;
  coordinateText: string = '';
  mouseCoordinates: {x: number, y: number} = {x: -1, y: -1};
  isImageLoading: boolean = false;
  imageError: string = '';
  
  // Horizontal line functionality
  horizontalLines: Array<{y: number, id: string, leftX: number, rightX: number, imageWidth: number, dbId?: number, isSaved?: boolean}> = [];
  showLineControls: boolean = false;
  hasUnsavedLines: boolean = false;
  isSavingLines: boolean = false;
  
  // Drag and drop state
  isDragging: boolean = false;
  dragLineId: string | null = null;
  dragStartY: number = 0;
  dragOffset: number = 0;
  
  // Fullscreen functionality
  isFullscreen: boolean = false;
  fullscreenImageUrl: string = '';
  
  // Bucket hierarchy management
  bucketHierarchy: BucketHierarchyNode[] = [];
  isLoadingHierarchy: boolean = false;
  selectedHierarchyNode: BucketHierarchyNode | null = null;
  currentBucketMode: 'upload' | 'view' | 'modify' | null = null;
  fileInput: HTMLInputElement | null = null;
  folderInput: HTMLInputElement | null = null;
  
  // GCP-style bucket browser
  folderBrowserCollapsed: boolean = false;
  currentFolderPath: string = '';
  fileFilter: string = '';
  sortColumn: 'name' | 'size' | 'lastModified' = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';
  allItemsSelected: boolean = false;
  someItemsSelected: boolean = false;
  
  // Multi-select for bulk operations
  selectedNodes: Set<string> = new Set();
  isMultiSelectMode: boolean = false;
  
  // Folder download progress
  isDownloadingFolder: boolean = false;
  downloadProgress: number = 0;
  downloadStatus: 'preparing' | 'downloading' | 'complete' | 'error' | 'idle' = 'idle';
  downloadingFolderName: string = '';
  
  private destroy$ = new Subject<void>();

  constructor(
    private bucketService: BucketService,
    private imageProcessingService: ImageProcessingService,
    private coordinateTransformService: CoordinateTransformationService,
    private uploadProgressService: UploadProgressService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.updateBreadcrumb();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.coordinateTransformService.clearTransformCache();
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any): void {
    // Clear transform cache and re-render lines on window resize
    this.coordinateTransformService.clearTransformCache();
    this.refreshHorizontalLines();
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen && this.currentImage) {
      this.fullscreenImageUrl = this.getCurrentImageDisplayUrl();
    }
    
    // Delay to allow DOM changes to complete, then refresh lines
    setTimeout(() => {
      if (this.coordinateTransformService) {
        this.coordinateTransformService.clearTransformCache();
      }
      this.refreshHorizontalLines();
      console.log(`🖥️ Fullscreen mode: ${this.isFullscreen ? 'ON' : 'OFF'} - Refreshed ${this.horizontalLines.length} lines`);
    }, 200);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only update when selectedNode changes, not for other inputs like showDetails
    if (changes['selectedNode']) {
      this.updateBreadcrumb();
      this.updateContentArea();
    };
  }

  private updateBreadcrumb(): void {
    this.breadcrumbItems = [];
    
    if (this.selectedNode) {
      // Build breadcrumb from selected node
      const items: BreadcrumbItem[] = [];
      
      // If it's an action node, add both operation and action
      if (this.selectedNode.type === 'action' && this.selectedNode.parent_id) {
        // Find parent operation
        const parentIcon = this.getOperationIcon(this.selectedNode.parent_id);
        items.push({
          name: this.getOperationName(this.selectedNode.parent_id),
          icon: parentIcon,
          type: 'operation'
        });
      }
      
      // Add current node
      items.push({
        name: this.selectedNode.name,
        icon: this.selectedNode.icon || 'description',
        type: this.selectedNode.type as 'operation' | 'action'
      });
      
      this.breadcrumbItems = items;
    }
  }

  private updateContentArea(): void {
    if (this.selectedNode) {
      // Show processed files for Python Scripts operations
      this.showProcessedFiles = this.selectedNode.parent_id === 'python-scripts' || 
                               this.selectedNode.node_id === 'python-scripts';
      
      // Load images for Image Operations
      if (this.isImageOperations()) {
        this.loadProcessingImages();
      }
      
      // Load bucket hierarchy for GCP operations
      if (this.isGcpUploadModify()) {
        this.loadBucketHierarchy();
      }
    }
  }

  private getOperationName(operationId: string): string {
    const operationNames: {[key: string]: string} = {
      'db-operations': 'DB Operations',
      'python-scripts': 'Python Scripts',
      'system-settings': 'System Settings'
    };
    return operationNames[operationId] || operationId;
  }

  private getOperationIcon(operationId: string): string {
    const operationIcons: {[key: string]: string} = {
      'db-operations': 'storage',
      'python-scripts': 'code',
      'system-settings': 'settings'
    };
    return operationIcons[operationId] || 'folder';
  }

  onFolderClick(folder: string): void {
    this.folderSelected.emit(folder);
  }

  onProcessFolder(): void {
    if (this.selectedFolder) {
      this.folderProcessed.emit(this.selectedFolder);
    }
  }

  onFileClick(file: ProcessedFile): void {
    this.selectedFile = file;
    this.fileSelected.emit(file);
  }

  onShowDetailsChange(): void {
    this.showDetailsToggled.emit(true);
  }

  onRefreshBuckets(): void {
    this.bucketsRefreshed.emit();
  }

  getCurrentOperation(): string {
    if (!this.selectedNode) return 'No Operation Selected';
    
    if (this.selectedNode.type === 'operation') {
      return this.selectedNode.name;
    } else if (this.selectedNode.type === 'action' && this.selectedNode.parent_id) {
      return `${this.getOperationName(this.selectedNode.parent_id)} → ${this.selectedNode.name}`;
    }
    
    return this.selectedNode.name;
  }

  getCurrentOperationDescription(): string {
    if (!this.selectedNode) return 'Select an operation from the tree navigation.';
    return this.selectedNode.description || 'No description available.';
  }

  getOperationInstructions(): string {
    if (!this.selectedNode) return '';
    
    const instructions: {[key: string]: string} = {
      'load-data': 'Upload and load data files into the database. Ensure data format is correct before proceeding.',
      'insert-data': 'Insert new records into the database. Validate data integrity before insertion.',
      'modify-data': 'Modify existing database records. Review changes carefully before applying.',
      'delete-rows': 'Delete specific rows from database tables. This action cannot be undone.',
      'google-vision-ocr': 'Process images using Google Vision API. Select folder and configure OCR settings.',
      'tesseract-ocr': 'Process images using Tesseract OCR engine. Adjust language and processing parameters.',
      'mistral-ocr': 'Process images using Mistral AI OCR. Configure AI model settings for optimal results.',
      'watermark-removal': 'Remove watermarks from images in the selected folder. Select folder and process images to remove watermarks.',
      'image-operations': 'View and process images with coordinate tracking and navigation tools. Toggle Image Processing mode to start.',
      'general-settings': 'Configure general system settings including UI preferences and default values.',
      'security-settings': 'Manage user access, permissions, and security configurations.',
      'backup-restore': 'Create system backups or restore from existing backup files.',
      'maintenance': 'Perform system maintenance tasks including cleanup and optimization.'
    };
    
    return instructions[this.selectedNode.node_id] || 'Follow the interface instructions for this operation.';
  }

  isPythonScriptOperation(): boolean {
    return this.selectedNode?.parent_id === 'python-scripts' || this.selectedNode?.node_id === 'python-scripts';
  }

  isGoogleVisionOCR(): boolean {
    return this.selectedNode?.node_id === 'google-vision-ocr';
  }

  isTesseractOCR(): boolean {
    return this.selectedNode?.node_id === 'tesseract-ocr';
  }

  isMistralOCR(): boolean {
    return this.selectedNode?.node_id === 'mistral-ocr';
  }

  isWatermarkRemoval(): boolean {
    return this.selectedNode?.node_id === 'watermark-removal';
  }

  isImageProcessingTool(): boolean {
    return this.selectedNode?.node_id === 'image-processing-tool' || 
           this.selectedNode?.node_id === 'load-image-processing-tool';
  }

  isDatabaseOperation(): boolean {
    return this.selectedNode?.parent_id === 'db-operations' || this.selectedNode?.node_id === 'db-operations';
  }

  isSystemSettingsOperation(): boolean {
    return this.selectedNode?.parent_id === 'system-settings' || this.selectedNode?.node_id === 'system-settings';
  }

  isGcpOperation(): boolean {
    return this.selectedNode?.parent_id === 'gcp-operations' || 
           this.selectedNode?.node_id === 'gcp-operations';
  }

  isGcpUploadModify(): boolean {
    return this.isGcpOperation() && 
           (this.selectedNode?.name?.toLowerCase().includes('upload') || 
            this.selectedNode?.name?.toLowerCase().includes('modify') || false);
  }

  getStatusMessage(): string {
    if (this.isLoading) {
      if (this.bucketFolders.length === 0) {
        return 'Loading folders...';
      } else if (this.selectedFolder) {
        return 'Processing folder...';
      } else {
        return 'Loading...';
      }
    }
    return '';
  }

  getOCRDescription(): string {
    if (this.isGoogleVisionOCR()) {
      return 'High-accuracy text detection with advanced language auto-detection. Best for multi-language documents and complex layouts.';
    } else if (this.isTesseractOCR()) {
      return 'Free, open-source OCR engine with multiple language support. Best for high contrast text and printed documents.';
    } else if (this.isMistralOCR()) {
      return 'Advanced AI-powered text recognition with context understanding. Best for complex layouts and handwritten text.';
    }
    return 'OCR service description not available.';
  }

  getFileStatusText(file: ProcessedFile): string {
    // When reprocess_count > 0, show "Re-processed"
    if (file.reprocess_count && file.reprocess_count > 0) {
      return 'Re-processed';
    }
    
    // When reprocess_count = 0 and has watermark_removed_url, don't show status (return empty)
    if (file.watermark_removed_url && (!file.reprocess_count || file.reprocess_count === 0)) {
      return '';
    }
    
    // When no watermark_removed_url, show "Original"
    if (!file.watermark_removed_url) {
      return 'Original';
    }
    
    return '';
  }

  getFileStatusIcon(file: ProcessedFile): string {
    // When reprocess_count > 0, show refresh icon
    if (file.reprocess_count && file.reprocess_count > 0) {
      return 'refresh';
    }
    
    // When has watermark_removed_url but reprocess_count = 0, show check_circle
    if (file.watermark_removed_url && (!file.reprocess_count || file.reprocess_count === 0)) {
      return 'check_circle';
    }
    
    // When no watermark_removed_url, show schedule icon
    if (!file.watermark_removed_url) {
      return 'schedule';
    }
    
    return 'check_circle';
  }

  // ================================
  // IMAGE OPERATIONS METHODS
  // ================================

  isImageOperations(): boolean {
    return this.selectedNode?.node_id === 'image-operations' || this.selectedNode?.parent_id === 'image-operations';
  }

  loadProcessingImages(): void {
    if (this.isLoadingImages) return;
    
    this.isLoadingImages = true;
    this.imageProcessingService.getProcessingImages(1, 100)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.processingImages = response.data.images;
            if (this.processingImages.length > 0) {
              this.currentImageIndex = 0;
              this.currentImage = this.processingImages[0];
              this.resetImageState();
            }
          }
          this.isLoadingImages = false;
        },
        error: (error) => {
          console.error('Error loading processing images:', error);
          this.isLoadingImages = false;
        }
      });
  }

  onImageProcessingModeToggle(enabled: boolean): void {
    this.imageProcessingMode = enabled;
    if (enabled && this.processingImages.length === 0) {
      this.loadProcessingImages();
    }
  }

  onPreviousImage(): void {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
      this.currentImage = this.processingImages[this.currentImageIndex];
      this.resetImageState();
      // Clear lines when changing images
      this.clearAllHorizontalLines();
    }
  }

  onNextImage(): void {
    if (this.currentImageIndex < this.processingImages.length - 1) {
      this.currentImageIndex++;
      this.currentImage = this.processingImages[this.currentImageIndex];
      this.resetImageState();
      // Clear lines when changing images
      this.clearAllHorizontalLines();
    }
  }

  resetImageState(): void {
    this.isImageLoading = true;
    this.imageError = '';
    this.mouseCoordinates = { x: -1, y: -1 };
    this.clearJumpMarker();
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Get image dimensions as formatted string
  getImageDimensions(image: ProcessingImage): string {
    if (image.image_width && image.image_height) {
      return `${image.image_width} × ${image.image_height}`;
    }
    return 'Unknown';
  }

  onCoordinateTextChange(text: string): void {
    this.coordinateText = text;
  }

  onImageMouseMove(event: MouseEvent): void {
    const target = event.target as HTMLImageElement;
    if (target && target.naturalWidth && target.naturalHeight) {
      // Use coordinate transformation service
      const naturalCoords = this.coordinateTransformService.mouseEventToNaturalCoords(event, target);
      const clampedCoords = this.coordinateTransformService.clampToImageBounds(
        naturalCoords,
        { width: target.naturalWidth, height: target.naturalHeight }
      );
      
      this.mouseCoordinates = clampedCoords;
    }
  }

  onImageClick(event: MouseEvent): void {
    if (this.showLineControls) {
      const target = event.target as HTMLImageElement;
      if (target && target.naturalWidth && target.naturalHeight) {
        // Use coordinate transformation service
        const naturalCoords = this.coordinateTransformService.mouseEventToNaturalCoords(event, target);
        const clampedCoords = this.coordinateTransformService.clampToImageBounds(
          naturalCoords,
          { width: target.naturalWidth, height: target.naturalHeight }
        );
        
        this.addHorizontalLine(clampedCoords.y, target);
        console.log(`🎯 Added horizontal line at y: ${clampedCoords.y}, x: ${clampedCoords.x}`);
      }
    }
  }

  onImageLoad(event: Event): void {
    this.isImageLoading = false;
    this.imageError = '';
    console.log('Image loaded successfully:', this.currentImage?.image_name);
    
    // Refresh horizontal lines after image loads
    setTimeout(() => {
      this.refreshHorizontalLines();
      this.loadExistingLines();
    }, 100);
  }

  private loadExistingLines(): void {
    if (!this.currentImage) return;

    this.imageProcessingService.getImageLines(this.currentImage.id, 'current_user')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            console.log(`📂 Loading ${response.data.length} existing lines for image ${this.currentImage?.id}`);
            
            // Clear current lines first
            this.clearAllHorizontalLines();
            
            // Add each stored line to the display
            response.data.forEach(dbLine => {
              const lineId = `line-db-${dbLine.id}`;
              const lineData = {
                y: dbLine.y_coordinate,
                id: lineId,
                leftX: dbLine.left_x_coordinate,
                rightX: dbLine.right_x_coordinate,
                imageWidth: dbLine.image_width,
                dbId: dbLine.id,
                isSaved: true
              };
              
              this.horizontalLines.push(lineData);
              this.renderHorizontalLine(lineData);
            });
            
            // Don't mark as unsaved since these are loaded from database
            this.hasUnsavedLines = false;
            console.log(`✅ Loaded ${response.data.length} lines from database`);
          }
        },
        error: (error) => {
          console.error('❌ Error loading existing lines:', error);
        }
      });
  }

  onImageError(event: Event): void {
    this.isImageLoading = false;
    const target = event.target as HTMLImageElement;
    this.imageError = `Failed to load: ${target.src}`;
    console.error('Image load error:', this.imageError);
  }

  onSyncImages(): void {
    this.isLoadingImages = true;
    this.imageProcessingService.syncImagesFromGCS()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            console.log('Images synced:', response.data);
            this.loadProcessingImages(); // Reload images after sync
          }
          this.isLoadingImages = false;
        },
        error: (error) => {
          console.error('Error syncing images:', error);
          this.isLoadingImages = false;
        }
      });
  }

  getCurrentImageDisplayUrl(): string {
    if (!this.currentImage) return '';
    return this.imageProcessingService.getImageDisplayUrl(this.currentImage.id);
  }

  getImageInfo(): string {
    if (!this.currentImage) return '';
    
    const info = [
      `Image ${this.currentImageIndex + 1} of ${this.processingImages.length}`,
      this.currentImage.image_name,
      this.imageProcessingService.getImageDimensions(this.currentImage),
      this.imageProcessingService.formatFileSize(this.currentImage.file_size || 0)
    ].filter(Boolean);
    
    return info.join(' • ');
  }

  hasPreviousImage(): boolean {
    return this.currentImageIndex > 0;
  }

  hasNextImage(): boolean {
    return this.currentImageIndex < this.processingImages.length - 1;
  }

  getImageInstructions(): string {
    return 'Toggle Image Processing mode to view and interact with images. Use Previous/Next buttons to navigate. Hover over images to see coordinates.';
  }

  // ================================
  // COORDINATE JUMP FUNCTIONALITY
  // ================================

  // Jump to coordinates functionality
  jumpToCoordinates(): void {
    const coords = this.parseCoordinatesFromText(this.coordinateText);
    if (coords) {
      console.log(`🎯 Jumping to coordinates: (${coords.x}, ${coords.y})`);
      this.addJumpMarker(coords.x, coords.y);
      this.scrollToCoordinates(coords.x, coords.y);
    } else {
      console.warn('❌ No valid coordinates found in text:', this.coordinateText);
    }
  }

  // Parse coordinates from text input
  parseCoordinatesFromText(text: string): {x: number, y: number} | null {
    if (!text) return null;
    
    // Match patterns like (100, 200), 100,200, 100 200, etc.
    const patterns = [
      /\((\d+),\s*(\d+)\)/,  // (100, 200)
      /(\d+),\s*(\d+)/,      // 100, 200
      /(\d+)\s+(\d+)/,       // 100 200
      /x:\s*(\d+).*y:\s*(\d+)/i, // x: 100 y: 200
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        const x = parseInt(match[1]);
        const y = parseInt(match[2]);
        if (!isNaN(x) && !isNaN(y)) {
          return { x, y };
        }
      }
    }
    return null;
  }

  // Add visual marker at coordinates
  addJumpMarker(x: number, y: number): void {
    this.clearJumpMarker();
    
    const imageContainer = this.isFullscreen 
      ? document.querySelector('.fullscreen-image-container') 
      : document.querySelector('.image-container');
    const image = this.isFullscreen
      ? document.querySelector('.fullscreen-image') as HTMLImageElement
      : document.querySelector('.processing-image') as HTMLImageElement;
    
    if (imageContainer && image && image.naturalWidth && image.naturalHeight) {
      const containerRect = imageContainer.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      
      // Calculate the position relative to the image container
      const imageOffsetX = (containerRect.width - imageRect.width) / 2;
      const imageOffsetY = (containerRect.height - imageRect.height) / 2;
      
      // Calculate marker position based on image natural dimensions
      const markerX = imageOffsetX + (x * imageRect.width / image.naturalWidth);
      const markerY = imageOffsetY + (y * imageRect.height / image.naturalHeight);
      
      console.log(`📍 Placing marker at screen position: (${Math.round(markerX)}, ${Math.round(markerY)})`);
      console.log(`📏 Image dimensions: ${image.naturalWidth}x${image.naturalHeight}, Displayed: ${Math.round(imageRect.width)}x${Math.round(imageRect.height)}`);
      
      // Create marker element
      const marker = document.createElement('div');
      marker.className = 'coordinate-jump-marker';
      marker.style.position = 'absolute';
      marker.style.left = `${markerX - 20}px`; // Center the 40px marker
      marker.style.top = `${markerY - 20}px`;  // Center the 40px marker
      marker.style.zIndex = '1000';
      marker.style.pointerEvents = 'none'; // Don't interfere with mouse events
      
      // Add click handler to remove marker
      marker.addEventListener('click', () => this.clearJumpMarker());
      
      imageContainer.appendChild(marker);
      
      // Auto-remove marker after 8 seconds (longer to see it better)
      setTimeout(() => this.clearJumpMarker(), 8000);
      
      // Also update mouse coordinates to show the jumped position
      this.mouseCoordinates = { x, y };
    }
  }

  // Clear jump marker
  clearJumpMarker(): void {
    const marker = document.querySelector('.coordinate-jump-marker');
    if (marker) {
      marker.remove();
    }
  }

  // Scroll to coordinates in image (for future use if image is scrollable)
  scrollToCoordinates(x: number, y: number): void {
    const imageContainer = document.querySelector('.image-container');
    const image = document.querySelector('.processing-image') as HTMLImageElement;
    
    if (imageContainer && image && image.naturalWidth && image.naturalHeight) {
      // For now, just center the view on the coordinates if the image is larger than container
      // This functionality can be expanded if scrolling is needed
      console.log(`Jumping to coordinates: (${x}, ${y})`);
    }
  }

  // Check if text contains valid coordinates
  hasValidCoordinates(): boolean {
    return this.parseCoordinatesFromText(this.coordinateText) !== null;
  }

  // ================================
  // HORIZONTAL LINE FUNCTIONALITY
  // ================================

  toggleLineControls(): void {
    this.showLineControls = !this.showLineControls;
    if (!this.showLineControls) {
      this.clearAllHorizontalLines();
    }
    console.log(`Line controls ${this.showLineControls ? 'enabled' : 'disabled'}`);
  }

  addHorizontalLine(y: number, imageElement?: HTMLImageElement): void {
    const image = imageElement || document.querySelector('.processing-image') as HTMLImageElement;
    if (!image) return;

    // Check if line already exists at this Y coordinate (within 5px tolerance)
    const existingLine = this.horizontalLines.find(line => Math.abs(line.y - y) < 5);
    if (existingLine) {
      console.log(`Line already exists at y: ${y}`);
      return;
    }

    // Get current image dimensions
    const imageWidth = image.naturalWidth;
    const leftX = 0;
    const rightX = imageWidth - 1;

    const lineId = `line-${Date.now()}`;
    const lineData = { y, id: lineId, leftX, rightX, imageWidth, isSaved: false };
    this.horizontalLines.push(lineData);
    this.renderHorizontalLine(lineData);
    this.hasUnsavedLines = true; // Mark as having unsaved changes
    console.log(`Added horizontal line at y: ${y}, left: (${leftX}, ${y}), right: (${rightX}, ${y}) [UNSAVED]`);
  }

  private storeLineInDatabase(y: number, leftX: number, rightX: number, imageWidth: number): void {
    if (!this.currentImage) return;

    const lineData = {
      y_coordinate: y,
      left_x_coordinate: leftX,
      right_x_coordinate: rightX,
      image_width: imageWidth,
      line_label: `Horizontal line at y:${y}`,
      user_id: 'current_user', // You can get this from auth service
      session_id: this.generateSessionId()
    };

    this.imageProcessingService.storeImageLine(this.currentImage.id, lineData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            console.log('✅ Line stored in database:', response.data);
          }
        },
        error: (error) => {
          console.error('❌ Error storing line:', error);
        }
      });
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  addHorizontalLineFromInput(): void {
    const coords = this.parseCoordinatesFromText(this.coordinateText);
    if (coords) {
      this.addHorizontalLine(coords.y);
    } else {
      // Try to parse just a Y coordinate
      const yCoord = this.parseYCoordinateFromText(this.coordinateText);
      if (yCoord !== null) {
        this.addHorizontalLine(yCoord);
      } else {
        console.warn('❌ No valid Y coordinate found in text:', this.coordinateText);
      }
    }
  }

  parseYCoordinateFromText(text: string): number | null {
    if (!text) return null;
    
    // Match patterns like y:100, Y:100, just 100, etc.
    const patterns = [
      /y:\s*(\d+)/i,        // y: 100
      /(\d+)/,              // just 100
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        const y = parseInt(match[1]);
        if (!isNaN(y)) {
          return y;
        }
      }
    }
    return null;
  }

  renderHorizontalLine(lineData: {y: number, id: string, leftX: number, rightX: number, imageWidth: number}): void {
    // Select the appropriate container based on fullscreen mode
    const imageContainer = this.isFullscreen 
      ? document.querySelector('.fullscreen-image-container') 
      : document.querySelector('.image-container');
    const image = this.isFullscreen
      ? document.querySelector('.fullscreen-image') as HTMLImageElement
      : document.querySelector('.processing-image') as HTMLImageElement;
    
    if (imageContainer && image && image.naturalWidth && image.naturalHeight) {
      const mode = this.isFullscreen ? 'fullscreen' : 'normal';
      console.log(`📐 Rendering line y=${lineData.y} in ${mode} mode`);
      console.log(`📐 Container:`, imageContainer.className);
      console.log(`📐 Image:`, image.className, `${image.naturalWidth}x${image.naturalHeight}`);
      
      const containerRect = imageContainer.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      
      // Calculate the position relative to the image container
      const imageOffsetX = (containerRect.width - imageRect.width) / 2;
      const imageOffsetY = (containerRect.height - imageRect.height) / 2;
      
      // Calculate line position based on image natural dimensions
      const lineY = imageOffsetY + (lineData.y * imageRect.height / image.naturalHeight);
      
      console.log(`📐 Container size: ${containerRect.width}x${containerRect.height}`);
      console.log(`📐 Image size: ${imageRect.width}x${imageRect.height}`);
      console.log(`📐 Line position: x=${imageOffsetX}, y=${lineY}`);
      
      // Create horizontal line element
      const line = document.createElement('div');
      line.className = 'horizontal-coordinate-line';
      line.id = lineData.id;
      line.style.position = 'absolute';
      line.style.left = `${imageOffsetX}px`;
      line.style.top = `${lineY - 1}px`; // Center the 2px line
      line.style.width = `${imageRect.width}px`;
      line.style.height = '2px';
      line.style.backgroundColor = '#00ff00';
      line.style.border = '1px solid #ffffff';
      line.style.boxShadow = '0 0 10px rgba(0, 255, 0, 0.8)';
      line.style.zIndex = '999';
      line.style.pointerEvents = 'auto';
      line.style.cursor = 'grab';
      
      // Add detailed coordinate label showing left and right endpoints with delete button
      const label = document.createElement('div');
      label.className = 'line-coordinate-label';
      label.innerHTML = `
        <div style="font-weight: bold; margin-bottom: 2px; display: flex; justify-content: space-between; align-items: center;">
          <span>y: ${lineData.y}</span>
          <button class="delete-line-btn" data-line-id="${lineData.id}" style="
            background: #ff4444; 
            color: white; 
            border: none; 
            border-radius: 3px; 
            width: 18px; 
            height: 18px; 
            font-size: 12px; 
            cursor: pointer; 
            display: flex; 
            align-items: center; 
            justify-content: center;
            margin-left: 8px;
          " title="Delete this line">×</button>
        </div>
        <div style="font-size: 10px;">Left: (${lineData.leftX}, ${lineData.y})</div>
        <div style="font-size: 10px;">Right: (${lineData.rightX}, ${lineData.y})</div>
      `;
      label.style.position = 'absolute';
      label.style.right = '-140px'; // Wider for delete button
      label.style.top = '-25px'; // Higher for more content
      label.style.fontSize = '11px';
      label.style.background = 'rgba(0, 255, 0, 0.95)';
      label.style.color = '#000000';
      label.style.padding = '4px 8px';
      label.style.borderRadius = '6px';
      label.style.fontWeight = 'bold';
      label.style.whiteSpace = 'nowrap';
      label.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.3)';
      label.style.border = '1px solid #ffffff';
      label.style.pointerEvents = 'auto'; // Enable pointer events for delete button
      
      // Add click handler for delete button
      const deleteBtn = label.querySelector('.delete-line-btn') as HTMLButtonElement;
      if (deleteBtn) {
        deleteBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const lineIdToDelete = deleteBtn.getAttribute('data-line-id');
          if (lineIdToDelete) {
            this.deleteSpecificLine(lineIdToDelete);
          }
        });
      }
      
      // Add drag and drop functionality (only in non-fullscreen mode)
      if (!this.isFullscreen) {
        this.addDragFunctionality(line, lineData);
      }
      
      line.appendChild(label);
      imageContainer.appendChild(line);
      
      console.log(`📏 Rendered horizontal line at y: ${lineData.y}, left: (${lineData.leftX}, ${lineData.y}), right: (${lineData.rightX}, ${lineData.y})`);
    } else {
      const mode = this.isFullscreen ? 'fullscreen' : 'normal';
      console.log(`❌ Failed to find container/image in ${mode} mode:`, {
        container: !!imageContainer,
        image: !!image,
        imageNatural: image ? `${image.naturalWidth}x${image.naturalHeight}` : 'none'
      });
    }
  }

  removeHorizontalLine(lineId: string): void {
    this.horizontalLines = this.horizontalLines.filter(line => line.id !== lineId);
    const lineElement = document.getElementById(lineId);
    if (lineElement) {
      // Clean up drag event listeners
      if ((lineElement as any)._dragCleanup) {
        (lineElement as any)._dragCleanup();
      }
      lineElement.remove();
    }
    
    // Update unsaved changes flag
    this.updateUnsavedChangesFlag();
  }

  deleteSpecificLine(lineId: string): void {
    const lineIndex = this.horizontalLines.findIndex(line => line.id === lineId);
    if (lineIndex === -1) {
      console.log('Line not found:', lineId);
      return;
    }

    const line = this.horizontalLines[lineIndex];
    
    if (line.dbId && line.isSaved) {
      // Line exists in database, delete from database first
      if (!this.currentImage) return;
      
      this.imageProcessingService.deleteImageLine(this.currentImage.id, line.dbId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              console.log(`✅ Deleted line ${line.dbId} from database`);
              this.removeHorizontalLine(lineId);
            }
          },
          error: (error) => {
            console.error('❌ Error deleting line from database:', error);
          }
        });
    } else {
      // Line not saved to database yet, just remove locally
      this.removeHorizontalLine(lineId);
      console.log(`🗑️ Removed unsaved line at y: ${line.y}`);
    }
  }

  private updateUnsavedChangesFlag(): void {
    this.hasUnsavedLines = this.horizontalLines.some(line => !line.isSaved);
  }

  private addDragFunctionality(lineElement: HTMLElement, lineData: {y: number, id: string, leftX: number, rightX: number, imageWidth: number, dbId?: number, isSaved?: boolean}): void {
    let isDragging = false;
    let startY = 0;
    let startMouseY = 0;
    let currentY = lineData.y;

    // Mouse down event
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target !== lineElement && !target?.classList?.contains('line-coordinate-label')) {
        return; // Only start drag on the line itself, not the label
      }
      
      isDragging = true;
      startY = currentY;
      startMouseY = e.clientY;
      this.isDragging = true;
      this.dragLineId = lineData.id;
      
      lineElement.style.cursor = 'grabbing';
      lineElement.style.zIndex = '1000';
      lineElement.classList.add('dragging');
      
      e.preventDefault();
      e.stopPropagation();
    };

    // Mouse move event (document level)
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging || this.dragLineId !== lineData.id) return;

      const image = document.querySelector('.processing-image') as HTMLImageElement;
      const imageContainer = document.querySelector('.image-container');
      
      if (!image || !imageContainer || !image.naturalHeight) return;

      const containerRect = imageContainer.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      
      // Calculate how much the mouse moved
      const deltaY = e.clientY - startMouseY;
      
      // Convert pixel movement to image coordinate movement
      const imageRatio = image.naturalHeight / imageRect.height;
      const coordinateDelta = deltaY * imageRatio;
      
      // Calculate new Y coordinate
      const newY = Math.round(Math.max(0, Math.min(image.naturalHeight - 1, startY + coordinateDelta)));
      
      // Update the visual position immediately
      const imageOffsetY = (containerRect.height - imageRect.height) / 2;
      const newLineY = imageOffsetY + (newY * imageRect.height / image.naturalHeight);
      lineElement.style.top = `${newLineY - 1}px`;
      
      // Update the label with new coordinates
      const label = lineElement.querySelector('.line-coordinate-label') as HTMLElement;
      if (label) {
        label.innerHTML = `
          <div style="font-weight: bold; margin-bottom: 2px; display: flex; justify-content: space-between; align-items: center;">
            <span>y: ${newY}</span>
            <button class="delete-line-btn" data-line-id="${lineData.id}" style="
              background: #ff4444; 
              color: white; 
              border: none; 
              border-radius: 3px; 
              width: 18px; 
              height: 18px; 
              font-size: 12px; 
              cursor: pointer; 
              display: flex; 
              align-items: center; 
              justify-content: center;
              margin-left: 8px;
            " title="Delete this line">×</button>
          </div>
          <div style="font-size: 10px;">Left: (${lineData.leftX}, ${newY})</div>
          <div style="font-size: 10px;">Right: (${lineData.rightX}, ${newY})</div>
        `;
        
        // Re-attach delete button event
        const deleteBtn = label.querySelector('.delete-line-btn') as HTMLButtonElement;
        if (deleteBtn) {
          deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const lineIdToDelete = deleteBtn.getAttribute('data-line-id');
            if (lineIdToDelete) {
              this.deleteSpecificLine(lineIdToDelete);
            }
          });
        }
      }
      
      currentY = newY;
    };

    // Mouse up event (document level)
    const onMouseUp = (e: MouseEvent) => {
      if (!isDragging || this.dragLineId !== lineData.id) return;

      isDragging = false;
      this.isDragging = false;
      this.dragLineId = null;
      
      lineElement.style.cursor = 'grab';
      lineElement.style.zIndex = '999';
      lineElement.classList.remove('dragging');
      
      // Update the line data in memory
      const lineIndex = this.horizontalLines.findIndex(line => line.id === lineData.id);
      if (lineIndex !== -1) {
        this.horizontalLines[lineIndex].y = currentY;
        
        // Mark as unsaved if it was previously saved
        if (this.horizontalLines[lineIndex].isSaved) {
          this.horizontalLines[lineIndex].isSaved = false;
          this.hasUnsavedLines = true;
          
          // If it has a database ID, update it in the database immediately
          if (this.horizontalLines[lineIndex].dbId && this.currentImage) {
            this.updateLineInDatabase(this.horizontalLines[lineIndex]);
          }
        } else {
          this.updateUnsavedChangesFlag();
        }
      }
      
      console.log(`📝 Line ${lineData.id} dragged to y: ${currentY}`);
    };

    // Attach event listeners
    lineElement.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    
    // Store cleanup function
    (lineElement as any)._dragCleanup = () => {
      lineElement.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }

  private updateLineInDatabase(line: {y: number, id: string, leftX: number, rightX: number, imageWidth: number, dbId?: number, isSaved?: boolean}): void {
    if (!line.dbId || !this.currentImage) return;

    const coordinates = {
      y_coordinate: line.y,
      left_x_coordinate: line.leftX,
      right_x_coordinate: line.rightX
    };

    this.imageProcessingService.updateImageLineCoordinates(this.currentImage.id, line.dbId, coordinates)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            console.log(`✅ Updated line ${line.dbId} coordinates in database`);
            line.isSaved = true;
            this.updateUnsavedChangesFlag();
          }
        },
        error: (error) => {
          console.error('❌ Error updating line coordinates:', error);
        }
      });
  }

  clearAllHorizontalLines(): void {
    this.horizontalLines.forEach(line => {
      const lineElement = document.getElementById(line.id);
      if (lineElement) {
        // Clean up drag event listeners
        if ((lineElement as any)._dragCleanup) {
          (lineElement as any)._dragCleanup();
        }
        lineElement.remove();
      }
    });
    this.horizontalLines = [];
    this.hasUnsavedLines = false; // Reset unsaved flag
    console.log(`🗑️ Cleared all horizontal lines (mode: ${this.isFullscreen ? 'fullscreen' : 'normal'})`);
  }

  refreshHorizontalLines(): void {
    const mode = this.isFullscreen ? 'fullscreen' : 'normal';
    console.log(`🔄 Refreshing ${this.horizontalLines.length} lines in ${mode} mode...`);
    
    // Clear existing DOM elements
    this.horizontalLines.forEach(line => {
      const lineElement = document.getElementById(line.id);
      if (lineElement) {
        lineElement.remove();
        console.log(`🗑️ Removed existing line element: ${line.id}`);
      }
    });
    
    // Re-render all lines
    this.horizontalLines.forEach((line, index) => {
      console.log(`📏 Rendering line ${index + 1}/${this.horizontalLines.length}: y=${line.y} id=${line.id}`);
      this.renderHorizontalLine(line);
    });
    
    console.log(`✅ Line refresh complete in ${mode} mode`);
  }

  getHorizontalLinesText(): string {
    if (this.horizontalLines.length === 0) {
      return 'No lines';
    }
    return this.horizontalLines.map(line => 
      `y:${line.y} [${line.leftX}-${line.rightX}]`
    ).join(', ');
  }

  saveAllLines(): void {
    if (!this.currentImage || this.horizontalLines.length === 0) {
      console.log('No image selected or no lines to save');
      return;
    }

    if (this.isSavingLines) {
      console.log('Save operation already in progress');
      return;
    }

    this.isSavingLines = true;
    console.log(`💾 Saving ${this.horizontalLines.length} lines to database...`);

    // First, clear existing lines for this image and user
    this.imageProcessingService.deleteAllImageLines(this.currentImage.id, 'current_user')
      .pipe(
        takeUntil(this.destroy$),
        // Then save all current lines
        switchMap(() => {
          const savePromises = this.horizontalLines.map(line => 
            this.saveLineToDatabase(line.y, line.leftX, line.rightX, line.imageWidth)
          );
          return forkJoin(savePromises);
        })
      )
      .subscribe({
        next: (responses: any[]) => {
          const successCount = responses.filter((r: any) => r.success).length;
          console.log(`✅ Successfully saved ${successCount}/${this.horizontalLines.length} lines`);
          
          // Mark all lines as saved and update with database IDs
          responses.forEach((response: any, index: number) => {
            if (response.success && response.data && index < this.horizontalLines.length) {
              this.horizontalLines[index].isSaved = true;
              this.horizontalLines[index].dbId = response.data.id;
            }
          });
          
          this.hasUnsavedLines = false;
          this.isSavingLines = false;
        },
        error: (error) => {
          console.error('❌ Error saving lines:', error);
          this.isSavingLines = false;
        }
      });
  }

  private saveLineToDatabase(y: number, leftX: number, rightX: number, imageWidth: number): Observable<any> {
    if (!this.currentImage) return of({ success: false });

    const lineData = {
      y_coordinate: y,
      left_x_coordinate: leftX,
      right_x_coordinate: rightX,
      image_width: imageWidth,
      line_label: `Horizontal line at y:${y}`,
      user_id: 'current_user',
      session_id: this.generateSessionId()
    };

    return this.imageProcessingService.storeImageLine(this.currentImage.id, lineData);
  }

  // ================================
  // BUCKET HIERARCHY METHODS
  // ================================

  loadBucketHierarchy(): void {
    if (!this.isGcpUploadModify()) return;
    
    this.isLoadingHierarchy = true;
    this.bucketService.getBucketHierarchy(this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: BucketHierarchyResponse) => {
          console.log('🗂️ Bucket hierarchy loaded:', response);
          // Apply Windows Explorer-style sorting and set default states
          this.bucketHierarchy = this.setDefaultFolderStates(response.hierarchy);
          this.isLoadingHierarchy = false;
          console.log('📁 Hierarchy sorted: folders first, then files');
        },
        error: (error) => {
          console.error('❌ Error loading bucket hierarchy:', error);
          this.isLoadingHierarchy = false;
        }
      });
  }

  private setDefaultFolderStates(nodes: BucketHierarchyNode[]): BucketHierarchyNode[] {
    // Sort nodes: folders first, then files (like Windows Explorer)
    const sortedNodes = this.sortNodesLikeWindowsExplorer(nodes);
    
    return sortedNodes.map(node => {
      const updatedNode = { ...node };
      
      // Set all folders to collapsed by default
      if (updatedNode.type === 'folder') {
        updatedNode.expanded = false;
      }
      
      // Recursively process children
      if (updatedNode.children && updatedNode.children.length > 0) {
        updatedNode.children = this.setDefaultFolderStates(updatedNode.children);
      }
      
      return updatedNode;
    });
  }

  private sortNodesLikeWindowsExplorer(nodes: BucketHierarchyNode[]): BucketHierarchyNode[] {
    return nodes.sort((a, b) => {
      // First, sort by type: folders before files
      if (a.type !== b.type) {
        if (a.type === 'folder' && b.type === 'file') return -1;
        if (a.type === 'file' && b.type === 'folder') return 1;
      }
      
      // Then sort alphabetically by name (case-insensitive)
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  }

  onBucketModeChange(mode: 'upload' | 'view' | 'modify'): void {
    this.currentBucketMode = mode;
    if (mode === 'upload' || mode === 'modify') {
      this.loadBucketHierarchy();
    }
  }

  onHierarchyNodeClick(node: BucketHierarchyNode): void {
    // Toggle selection on click
    if (this.selectedNodes.has(node.path)) {
      this.selectedNodes.delete(node.path);
      if (this.selectedHierarchyNode === node) {
        const remaining = Array.from(this.selectedNodes);
        this.selectedHierarchyNode = remaining.length > 0 
          ? this.findNodeByPath(this.bucketHierarchy, remaining[0]) 
          : null;
      }
    } else {
      this.selectedNodes.add(node.path);
      this.selectedHierarchyNode = node;
    }
    this.updateSelectionState();
    console.log('📁 Selected hierarchy nodes:', Array.from(this.selectedNodes));
  }

  onCreateFolder(): void {
    if (!this.selectedHierarchyNode) {
      alert('Please select a folder first');
      return;
    }

    const folderName = prompt('Enter new folder name:');
    if (!folderName || folderName.trim() === '') {
      return;
    }

    const targetPath = this.selectedHierarchyNode.path;
    this.bucketService.createFolder(targetPath, folderName.trim(), this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ Folder created successfully:', response);
          this.loadBucketHierarchy(); // Refresh hierarchy
        },
        error: (error) => {
          console.error('❌ Error creating folder:', error);
          alert('Failed to create folder. Please try again.');
        }
      });
  }

  onBrowseFiles(): void {
    if (!this.selectedHierarchyNode) {
      alert('Please select a folder first');
      return;
    }

    // Create a file input element
    if (!this.fileInput) {
      this.fileInput = document.createElement('input');
      this.fileInput.type = 'file';
      this.fileInput.multiple = true;
      this.fileInput.accept = 'image/*,.pdf,.doc,.docx';
    }

    this.fileInput.onchange = (event: any) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.uploadSelectedFiles(Array.from(files));
      }
    };

    this.fileInput.click();
  }

  uploadSelectedFiles(files: File[]): void {
    if (!this.selectedHierarchyNode) {
      alert('Please select a folder first');
      return;
    }

    const targetPath = this.selectedHierarchyNode.path;
    console.log(`📤 Uploading ${files.length} files to: ${targetPath}`);

    // Use the upload progress service for visual feedback
    this.uploadProgressService.startUploadSession(files, targetPath, this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (session) => {
          console.log('📤 Upload session started:', session.id);
          
          // Subscribe to session completion
          this.uploadProgressService.currentSession$
            .pipe(takeUntil(this.destroy$))
            .subscribe(currentSession => {
              if (currentSession && currentSession.id === session.id && 
                  (currentSession.status === 'completed' || currentSession.status === 'failed')) {
                console.log('✅ Upload session completed, refreshing hierarchy');
                this.loadBucketHierarchy();
              }
            });
        },
        error: (error) => {
          console.error('❌ Error starting upload session:', error);
        }
      });
  }

  onDeleteSelected(): void {
    const selectedPaths = Array.from(this.selectedNodes);
    
    if (selectedPaths.length === 0) {
      alert('Please select file(s) or folder(s) first');
      return;
    }

    const itemCount = selectedPaths.length;
    const confirmMessage = itemCount === 1 
      ? `Are you sure you want to delete "${this.selectedHierarchyNode?.name}"?`
      : `Are you sure you want to delete ${itemCount} selected items?`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    this.bucketService.deleteItems(selectedPaths, this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log(`✅ ${itemCount} item(s) deleted successfully:`, response);
          this.selectedNodes.clear();
          this.selectedHierarchyNode = null;
          this.allItemsSelected = false;
          this.someItemsSelected = false;
          this.loadBucketHierarchy(); // Refresh hierarchy
        },
        error: (error) => {
          console.error('❌ Error deleting items:', error);
          alert('Failed to delete item(s). Please try again.');
        }
      });
  }

  onRefreshHierarchy(): void {
    // Store current expanded states before refresh
    const expandedStates = this.getExpandedStates(this.bucketHierarchy);
    this.loadBucketHierarchy();
    
    // Restore expanded states after a short delay to allow for data loading
    setTimeout(() => {
      this.restoreExpandedStates(this.bucketHierarchy, expandedStates);
    }, 100);
  }

  private getExpandedStates(nodes: BucketHierarchyNode[]): Map<string, boolean> {
    const expandedStates = new Map<string, boolean>();
    
    const traverse = (nodeList: BucketHierarchyNode[]) => {
      nodeList.forEach(node => {
        if (node.type === 'folder' && node.expanded !== undefined) {
          expandedStates.set(node.path, node.expanded);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    
    traverse(nodes);
    return expandedStates;
  }

  private restoreExpandedStates(nodes: BucketHierarchyNode[], expandedStates: Map<string, boolean>): void {
    const traverse = (nodeList: BucketHierarchyNode[]) => {
      // Apply Windows Explorer sorting to each level when restoring states
      const sortedNodes = this.sortNodesLikeWindowsExplorer(nodeList);
      
      sortedNodes.forEach(node => {
        if (node.type === 'folder' && expandedStates.has(node.path)) {
          node.expanded = expandedStates.get(node.path);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    
    traverse(nodes);
  }

  getNodeIcon(node: BucketHierarchyNode): string {
    return node.type === 'folder' ? 'folder' : 'insert_drive_file';
  }

  // ================================
  // NEW TREE INTERACTION METHODS
  // ================================

  onCreateFolderInPath(parentPath: string): void {
    const folderName = prompt('Enter new folder name:');
    if (!folderName || folderName.trim() === '') {
      return;
    }

    this.bucketService.createFolder(parentPath, folderName.trim(), this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ Folder created successfully:', response);
          this.loadBucketHierarchy(); // Refresh hierarchy
        },
        error: (error) => {
          console.error('❌ Error creating folder:', error);
          alert('Failed to create folder. Please try again.');
        }
      });
  }

  onDeleteNode(node: BucketHierarchyNode): void {
    const confirmMessage = `Are you sure you want to delete "${node.name}"?`;
    if (!confirm(confirmMessage)) {
      return;
    }

    const itemsToDelete = [node.path];
    this.bucketService.deleteItems(itemsToDelete, this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ Item deleted successfully:', response);
          if (this.selectedHierarchyNode === node) {
            this.selectedHierarchyNode = null;
          }
          this.loadBucketHierarchy(); // Refresh hierarchy
        },
        error: (error) => {
          console.error('❌ Error deleting item:', error);
          alert('Failed to delete item. Please try again.');
        }
      });
  }

  onUploadToFolder(folderPath: string): void {
    // Create a file input element
    if (!this.fileInput) {
      this.fileInput = document.createElement('input');
      this.fileInput.type = 'file';
      this.fileInput.multiple = true;
      this.fileInput.accept = 'image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls';
    }

    this.fileInput.onchange = (event: any) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.uploadFilesToPath(Array.from(files), folderPath);
      }
    };

    this.fileInput.click();
  }

  onPreviewFolder(node: BucketHierarchyNode): void {
    console.log('📂 Preview folder contents:', node);
    // Toggle expanded state for the folder
    this.toggleFolderExpansion(node);
  }

  toggleFolderExpansion(node: BucketHierarchyNode): void {
    if (node.type === 'folder' && node.children && node.children.length > 0) {
      node.expanded = !node.expanded;
      console.log(`📁 ${node.expanded ? 'Expanded' : 'Collapsed'} folder: ${node.name}`);
    }
  }

  private uploadFilesToPath(files: File[], targetPath: string): void {
    console.log(`📤 Uploading ${files.length} files to: ${targetPath}`);

    // Use the upload progress service for visual feedback
    this.uploadProgressService.startUploadSession(files, targetPath, this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (session) => {
          console.log('📤 Upload session started:', session.id);
          
          // Subscribe to session completion
          this.uploadProgressService.currentSession$
            .pipe(takeUntil(this.destroy$))
            .subscribe(currentSession => {
              if (currentSession && currentSession.id === session.id && 
                  (currentSession.status === 'completed' || currentSession.status === 'failed')) {
                console.log('✅ Upload session completed, refreshing hierarchy');
                this.loadBucketHierarchy();
              }
            });
        },
        error: (error) => {
          console.error('❌ Error starting upload session:', error);
        }
      });
  }

  // ================================
  // ROOT LEVEL BUCKET OPERATIONS
  // ================================

  onCreateRootFolder(): void {
    const folderName = prompt('Enter root folder name:');
    if (!folderName || folderName.trim() === '') {
      return;
    }

    // Create folder at root level (empty parent path)
    this.bucketService.createFolder('', folderName.trim(), this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ Root folder created successfully:', response);
          this.loadBucketHierarchy(); // Refresh hierarchy
          alert(`Root folder "${folderName}" created successfully!`);
        },
        error: (error) => {
          console.error('❌ Error creating root folder:', error);
          alert('Failed to create root folder. Please try again.');
        }
      });
  }

  onUploadToRoot(): void {
    // Create a file input element
    if (!this.fileInput) {
      this.fileInput = document.createElement('input');
      this.fileInput.type = 'file';
      this.fileInput.multiple = true;
      this.fileInput.accept = 'image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls';
    }

    this.fileInput.onchange = (event: any) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.uploadFilesToPath(Array.from(files), ''); // Empty string for root
      }
    };

    this.fileInput.click();
  }

  // ================================
  // FOLDER UPLOAD METHODS
  // ================================

  /**
   * Upload entire folder to root
   */
  onUploadFolderToRoot(): void {
    this.uploadFolderToPath('');
  }

  /**
   * Upload entire folder to selected folder
   */
  onUploadFolderToSelected(): void {
    if (!this.selectedHierarchyNode || this.selectedHierarchyNode.type !== 'folder') {
      alert('Please select a folder first');
      return;
    }
    this.uploadFolderToPath(this.selectedHierarchyNode.path);
  }

  /**
   * Upload folder to a specific path
   */
  uploadFolderToPath(targetPath: string): void {
    // Create folder input with webkitdirectory attribute
    if (!this.folderInput) {
      this.folderInput = document.createElement('input');
      this.folderInput.type = 'file';
      this.folderInput.setAttribute('webkitdirectory', '');
      this.folderInput.setAttribute('directory', '');
      this.folderInput.multiple = true;
    }

    this.folderInput.onchange = (event: any) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        const fileArray: File[] = Array.from(files);
        
        // Get the folder name from the first file's webkitRelativePath
        const firstFilePath = fileArray[0].webkitRelativePath || '';
        const folderName = firstFilePath.split('/')[0];
        
        if (folderName) {
          console.log(`📁 Uploading folder "${folderName}" with ${fileArray.length} files to: ${targetPath || 'root'}`);
          
          // Upload files with their relative paths preserved
          this.uploadFolderWithStructure(fileArray, targetPath, folderName);
        } else {
          console.error('❌ Could not determine folder name');
          alert('Could not determine folder structure. Please try again.');
        }
      }
      
      // Reset the input
      this.folderInput!.value = '';
    };

    this.folderInput.click();
  }

  /**
   * Upload folder with its internal structure preserved
   */
  private uploadFolderWithStructure(files: File[], targetBasePath: string, folderName: string): void {
    // Group files by their relative paths
    const filesWithPaths: { file: File; relativePath: string }[] = files.map(file => {
      const webkitPath = (file as any).webkitRelativePath || file.name;
      return {
        file: file,
        relativePath: webkitPath
      };
    });

    console.log(`📤 Uploading ${files.length} files with folder structure`);
    console.log(`📂 Target path: ${targetBasePath ? targetBasePath + '/' + folderName : folderName}`);

    // Use the upload progress service for visual feedback
    const fullTargetPath = targetBasePath ? `${targetBasePath}/${folderName}` : folderName;
    
    this.uploadProgressService.startUploadSession(files, fullTargetPath, this.bucketName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (session) => {
          console.log('📤 Folder upload session started:', session.id);
          
          // Subscribe to session completion
          this.uploadProgressService.currentSession$
            .pipe(takeUntil(this.destroy$))
            .subscribe(currentSession => {
              if (currentSession && currentSession.id === session.id && 
                  (currentSession.status === 'completed' || currentSession.status === 'failed')) {
                console.log('✅ Folder upload session completed, refreshing hierarchy');
                this.loadBucketHierarchy();
              }
            });
        },
        error: (error) => {
          console.error('❌ Error starting folder upload session:', error);
        }
      });
  }

  /**
   * Handle folder upload to a specific folder node (from quick actions)
   */
  onUploadFolderToNode(folderPath: string): void {
    this.uploadFolderToPath(folderPath);
  }

  // ================================
  // DOWNLOAD METHODS
  // ================================

  onDownloadFile(node: BucketHierarchyNode): void {
    if (node.type !== 'file') {
      console.error('❌ Cannot download: node is not a file');
      return;
    }

    console.log(`📥 Downloading file: ${node.name} from path: ${node.path}`);
    this.bucketService.downloadFile(node.path, this.bucketName);
  }

  onDownloadFolder(node: BucketHierarchyNode): void {
    if (node.type !== 'folder') {
      console.error('❌ Cannot download: node is not a folder');
      return;
    }

    // Don't start a new download if one is already in progress
    if (this.isDownloadingFolder) {
      this.snackBar.open('A folder download is already in progress', 'Close', { duration: 3000 });
      return;
    }

    console.log(`📥 Downloading folder as ZIP: ${node.name} from path: ${node.path}`);
    
    // Set download state
    this.isDownloadingFolder = true;
    this.downloadProgress = 0;
    this.downloadStatus = 'preparing';
    this.downloadingFolderName = node.name;
    
    // Show snackbar
    this.snackBar.open(`Preparing download for "${node.name}"...`, undefined, { duration: 0 });
    
    this.bucketService.downloadFolder(
      node.path, 
      this.bucketName,
      (progress, status) => {
        this.downloadProgress = progress;
        this.downloadStatus = status;
        
        if (status === 'downloading') {
          // Update snackbar message
          this.snackBar.open(`Downloading "${node.name}"... ${progress}%`, undefined, { duration: 0 });
        } else if (status === 'complete') {
          this.snackBar.open(`Download complete: "${node.name}.zip"`, 'Close', { duration: 5000 });
          this.isDownloadingFolder = false;
          this.downloadStatus = 'idle';
        } else if (status === 'error') {
          this.snackBar.open(`Download failed: "${node.name}"`, 'Close', { duration: 5000 });
          this.isDownloadingFolder = false;
          this.downloadStatus = 'idle';
        }
      }
    ).subscribe({
      error: (err) => {
        console.error('❌ Folder download failed:', err);
        this.isDownloadingFolder = false;
        this.downloadStatus = 'idle';
        this.snackBar.open('Folder download failed. Please try again.', 'Close', { duration: 5000 });
      }
    });
  }

  onDownloadSelectedItem(): void {
    // Get all selected items
    const selectedPaths = Array.from(this.selectedNodes);
    
    if (selectedPaths.length === 0) {
      console.error('❌ No items selected for download');
      return;
    }

    // Download each selected item
    console.log(`📥 Downloading ${selectedPaths.length} selected item(s)`);
    
    selectedPaths.forEach((path, index) => {
      const node = this.findNodeByPath(this.bucketHierarchy, path);
      if (node) {
        // Add small delay between downloads to avoid browser blocking
        setTimeout(() => {
          if (node.type === 'file') {
            this.onDownloadFile(node);
          } else if (node.type === 'folder') {
            this.onDownloadFolder(node);
          }
        }, index * 500); // 500ms delay between each download
      }
    });
  }

  /**
   * Get the count of selected items
   */
  getSelectedCount(): number {
    return this.selectedNodes.size;
  }

  /**
   * Clear all selections
   */
  onClearSelection(): void {
    this.selectedNodes.clear();
    this.selectedHierarchyNode = null;
    this.updateSelectionState();
    console.log('🧹 Selection cleared');
  }

  // ================================
  // ENTERPRISE HIERARCHY HELPERS
  // ================================

  /**
   * Get folder count from hierarchy
   */
  getFolderCount(): number {
    return this.countNodesByType(this.bucketHierarchy, 'folder');
  }

  /**
   * Get file count from hierarchy
   */
  getFileCount(): number {
    return this.countNodesByType(this.bucketHierarchy, 'file');
  }

  /**
   * Count nodes by type recursively
   */
  private countNodesByType(nodes: BucketHierarchyNode[], type: 'file' | 'folder'): number {
    let count = 0;
    nodes.forEach(node => {
      if (node.type === type) {
        count++;
      }
      if (node.children && node.children.length > 0) {
        count += this.countNodesByType(node.children, type);
      }
    });
    return count;
  }

  /**
   * Get tree level array for visual tree lines
   */
  getTreeLevelArray(level: number): number[] {
    return Array(level).fill(0).map((_, i) => i);
  }

  /**
   * Get enhanced icon based on file type
   */
  getEnterpriseNodeIcon(node: BucketHierarchyNode): string {
    if (node.type === 'folder') {
      if (node.expanded) {
        return 'folder_open';
      }
      return 'folder';
    }
    
    // File type icons based on extension
    const ext = node.name.split('.').pop()?.toLowerCase() || '';
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) {
      return 'image';
    }
    if (['pdf'].includes(ext)) {
      return 'picture_as_pdf';
    }
    if (['doc', 'docx'].includes(ext)) {
      return 'description';
    }
    if (['xls', 'xlsx'].includes(ext)) {
      return 'table_chart';
    }
    if (['txt', 'md'].includes(ext)) {
      return 'text_snippet';
    }
    if (['json', 'xml', 'yaml', 'yml'].includes(ext)) {
      return 'data_object';
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return 'folder_zip';
    }
    if (['mp4', 'avi', 'mov', 'wmv'].includes(ext)) {
      return 'video_file';
    }
    if (['mp3', 'wav', 'ogg', 'flac'].includes(ext)) {
      return 'audio_file';
    }
    
    return 'insert_drive_file';
  }

  /**
   * Track by function for ngFor
   */
  trackByPath(index: number, node: BucketHierarchyNode): string {
    return node.path;
  }

  // ================================
  // GCP-STYLE BUCKET BROWSER METHODS
  // ================================

  /**
   * Toggle folder browser panel visibility
   */
  toggleFolderBrowser(): void {
    this.folderBrowserCollapsed = !this.folderBrowserCollapsed;
  }

  /**
   * Get folders only from nodes (for left tree)
   */
  getFoldersOnly(nodes: BucketHierarchyNode[]): BucketHierarchyNode[] {
    return nodes.filter(node => node.type === 'folder');
  }

  /**
   * Navigate to bucket root
   */
  onNavigateToBucketRoot(): void {
    this.currentFolderPath = '';
    this.selectedHierarchyNode = null;
    this.selectedNodes.clear();
    this.updateSelectionState();
    this.fileFilter = '';
  }

  /**
   * Navigate to a specific folder
   */
  onNavigateToFolder(node: BucketHierarchyNode): void {
    if (node.type === 'folder') {
      this.currentFolderPath = node.path;
      this.selectedHierarchyNode = node;
      // Also add to selectedNodes so Download/Delete buttons are enabled
      this.selectedNodes.clear();
      this.selectedNodes.add(node.path);
      this.updateSelectionState();
      // Expand the folder if not already expanded
      if (!node.expanded) {
        node.expanded = true;
      }
      this.fileFilter = '';
    }
  }

  /**
   * Navigate to a specific path (from breadcrumb)
   */
  onNavigateToPath(path: string): void {
    const node = this.findNodeByPath(this.bucketHierarchy, path);
    if (node) {
      this.onNavigateToFolder(node);
    } else {
      this.currentFolderPath = path;
    }
  }

  /**
   * Find node by path recursively
   */
  private findNodeByPath(nodes: BucketHierarchyNode[], path: string): BucketHierarchyNode | null {
    for (const node of nodes) {
      if (node.path === path) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = this.findNodeByPath(node.children, path);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Get breadcrumbs for current path
   */
  getCurrentPathBreadcrumbs(): { name: string; path: string }[] {
    if (!this.currentFolderPath) return [];
    
    const parts = this.currentFolderPath.split('/').filter(p => p);
    const breadcrumbs: { name: string; path: string }[] = [];
    let currentPath = '';
    
    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      breadcrumbs.push({ name: part, path: currentPath });
    }
    
    return breadcrumbs;
  }

  /**
   * Copy current path to clipboard
   */
  onCopyCurrentPath(): void {
    const fullPath = `gs://${this.bucketName}/${this.currentFolderPath}`;
    navigator.clipboard.writeText(fullPath).then(() => {
      console.log('📋 Path copied:', fullPath);
    });
  }

  /**
   * Copy item path to clipboard
   */
  onCopyItemPath(item: BucketHierarchyNode): void {
    const fullPath = `gs://${this.bucketName}/${item.path}`;
    navigator.clipboard.writeText(fullPath).then(() => {
      console.log('📋 Item path copied:', fullPath);
    });
  }

  /**
   * Get current folder items (files and subfolders)
   */
  getCurrentFolderItems(): BucketHierarchyNode[] {
    if (!this.currentFolderPath) {
      return this.bucketHierarchy;
    }
    
    const currentNode = this.findNodeByPath(this.bucketHierarchy, this.currentFolderPath);
    return currentNode?.children || [];
  }

  /**
   * Get filtered current folder items
   */
  getFilteredCurrentFolderItems(): BucketHierarchyNode[] {
    let items = this.getCurrentFolderItems();
    
    // Apply filter
    if (this.fileFilter) {
      const filter = this.fileFilter.toLowerCase();
      items = items.filter(item => item.name.toLowerCase().includes(filter));
    }
    
    // Apply sorting
    items = [...items].sort((a, b) => {
      // Folders first
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;
      
      let comparison = 0;
      switch (this.sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = (a.size || 0) - (b.size || 0);
          break;
        case 'lastModified':
          const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          comparison = dateA - dateB;
          break;
      }
      
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return items;
  }

  /**
   * Sort items by column
   */
  onSortBy(column: 'name' | 'size' | 'lastModified'): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  /**
   * Handle item double-click (open folder or download file)
   */
  onItemDoubleClick(item: BucketHierarchyNode): void {
    if (item.type === 'folder') {
      this.onNavigateToFolder(item);
    } else {
      this.onDownloadFile(item);
    }
  }

  /**
   * Handle file click to preview in details panel
   */
  onPreviewFile(item: BucketHierarchyNode): void {
    if (item.type !== 'file') {
      return;
    }
    
    console.log('📄 Preview file:', item.path);
    console.log('📦 Bucket name:', this.bucketName);
    
    // Construct preview URL for the file
    const previewUrl = `${environment.apiBaseUrl}/api/gcs-files/${this.bucketName}/${item.path}`;
    console.log('🔗 Preview URL:', previewUrl);
    
    // Create a ProcessedFile object to emit for preview
    const processedFile: ProcessedFile = {
      page_id: item.name,
      original_page_url: previewUrl,  // Use preview URL as original for display
      watermark_removed_url: '',
      whitespace_cropped_url: '',
      footer_removed_url: '',
      footer_url: '',
      page_number_url: '',
      book_name: '',
      validation: 'Correct',
      selected_script: '',
      reprocess_count: 0,
      created_at: item.lastModified || '',
      path: item.path,
      folder_path: item.path.substring(0, item.path.lastIndexOf('/')),
      bucket_name: this.bucketName,
      size: item.size,
      lastModified: item.lastModified,
      type: 'file',
      preview_url: previewUrl
    };
    
    console.log('📋 ProcessedFile bucket_name:', processedFile.bucket_name);
    
    // Emit the file selection event for the parent to handle
    this.fileSelected.emit(processedFile);
    
    // Also show details panel if not already visible
    if (!this.showDetails) {
      this.showDetailsToggled.emit(true);
    }
  }

  /**
   * Handle select all checkbox
   */
  onSelectAllItems(checked: boolean): void {
    const items = this.getFilteredCurrentFolderItems();
    
    if (checked) {
      // Select all items
      items.forEach(item => this.selectedNodes.add(item.path));
      this.allItemsSelected = true;
      this.someItemsSelected = false;
      // Set the first item as the primary selected node for single actions
      if (items.length > 0) {
        this.selectedHierarchyNode = items[0];
      }
    } else {
      // Deselect all items
      items.forEach(item => this.selectedNodes.delete(item.path));
      this.allItemsSelected = false;
      this.someItemsSelected = false;
      this.selectedHierarchyNode = null;
    }
  }

  /**
   * Handle individual item checkbox
   */
  onItemCheckboxChange(item: BucketHierarchyNode, checked: boolean): void {
    if (checked) {
      this.selectedNodes.add(item.path);
      this.selectedHierarchyNode = item;
    } else {
      this.selectedNodes.delete(item.path);
      if (this.selectedHierarchyNode === item) {
        // Set another selected item as primary, or null if none
        const remainingPaths = Array.from(this.selectedNodes);
        if (remainingPaths.length > 0) {
          const firstPath = remainingPaths[0];
          this.selectedHierarchyNode = this.findNodeByPath(this.bucketHierarchy, firstPath);
        } else {
          this.selectedHierarchyNode = null;
        }
      }
    }
    this.updateSelectionState();
  }

  /**
   * Check if an item is selected
   */
  isItemSelected(item: BucketHierarchyNode): boolean {
    return this.selectedNodes.has(item.path);
  }

  /**
   * Update selection state for checkboxes
   */
  private updateSelectionState(): void {
    const items = this.getFilteredCurrentFolderItems();
    const selectedCount = items.filter(item => this.selectedNodes.has(item.path)).length;
    
    this.allItemsSelected = items.length > 0 && selectedCount === items.length;
    this.someItemsSelected = selectedCount > 0 && selectedCount < items.length;
  }

  /**
   * Get file type icon
   */
  getFileTypeIcon(filename: string): string {
    // Use partial node for icon lookup
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) {
      return 'image';
    }
    if (['pdf'].includes(ext)) {
      return 'picture_as_pdf';
    }
    if (['doc', 'docx'].includes(ext)) {
      return 'description';
    }
    if (['xls', 'xlsx'].includes(ext)) {
      return 'table_chart';
    }
    if (['txt', 'md'].includes(ext)) {
      return 'text_snippet';
    }
    if (['json', 'xml', 'yaml', 'yml'].includes(ext)) {
      return 'data_object';
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return 'folder_zip';
    }
    if (['mp4', 'avi', 'mov', 'wmv'].includes(ext)) {
      return 'video_file';
    }
    if (['mp3', 'wav', 'ogg', 'flac'].includes(ext)) {
      return 'audio_file';
    }
    
    return 'insert_drive_file';
  }

  /**
   * Get file type string
   */
  getFileType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    
    const typeMap: { [key: string]: string } = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'txt': 'text/plain',
      'json': 'application/json',
      'xml': 'application/xml',
      'zip': 'application/zip',
      'mp4': 'video/mp4',
      'mp3': 'audio/mpeg'
    };
    
    return typeMap[ext] || 'application/octet-stream';
  }

  /**
   * Navigate to previous or next bucket file
   * Returns the new ProcessedFile or null if navigation is not possible
   */
  navigateBucketFile(direction: 'previous' | 'next', currentFile: ProcessedFile | null): ProcessedFile | null {
    if (!currentFile) return null;
    
    // Get all files (not folders) from current view
    const items = this.getFilteredCurrentFolderItems().filter(item => item.type === 'file');
    if (items.length === 0) return null;
    
    // Find current file index by path
    const currentIndex = items.findIndex(item => item.path === currentFile.path);
    if (currentIndex === -1) return null;
    
    // Calculate new index
    let newIndex: number;
    if (direction === 'previous') {
      newIndex = currentIndex - 1;
      if (newIndex < 0) return null; // Already at first file
    } else {
      newIndex = currentIndex + 1;
      if (newIndex >= items.length) return null; // Already at last file
    }
    
    // Get the new item and preview it
    const newItem = items[newIndex];
    this.onPreviewFile(newItem);
    
    // Return the processed file (it will be emitted via fileSelected)
    return this.selectedFile;
  }
} 